import React, { useState } from 'react';

import TruvBridge from '@truv/react';

import { LoadingType, setLoadingState } from 'prequalificationV2/actions';
import { setLoanPortal } from 'prequalificationV2/actions/dashboardPortal';
import { useAppDispatch } from 'prequalificationV2/hooks/useAppDispatch';
import { useAppSelector } from 'prequalificationV2/hooks/useAppSelector';
import * as notificationManager from 'notifications';
import { FailureNotificationType } from 'notifications';
import { Button } from 'components/Buttons/Button';
import { ConnectBankAccountIcon } from 'assets/icons';
import { BankAccountsStatus } from 'enums/BankAccountsStatus';
import { truvApi } from 'api/TruvApi';
import { BorrowerRole } from 'enums/BorrowerRole';
import { getApplicationInfoForDashboardThunk } from 'prequalificationV2/thunks/getApplicationInfoForDashboard';
import SubSection, { SubSectionState } from 'prequalificationV2/components/LoanPortal/Sections/SubSection/SubSection';
import Section from 'prequalificationV2/components/LoanPortal/Sections/Section';
import styles from 'prequalificationV2/components/LoanPortal/FundingInformation/ConnectBankAccountSection/ConnectBankAccountSection.module.scss';

const VERIFICATION_ERROR_TYPE = 'ERROR';
const VERIFICATION_NO_DATA_ERROR_CODE = 'NO_DATA';

const ConnectBankAccountSection = () => {
  const dispatch = useAppDispatch();

  const { applicationId } = useAppSelector((state) => state.application);
  const { fundingInformationComplete, financialAccountAggregationAttemptComplete } = useAppSelector(
    (state) => state.dashboardPortal,
  );
  const [currentToken, setCurrentToken] = useState<string>('');
  const [isVerificationInProgress, setIsVerificationInProgress] = useState<boolean>(false);
  const [isVerificationWidgetOpened, setIsVerificationWidgetOpened] = useState<boolean>(false);
  const { borrowerId } = useAppSelector((state) => state.borrower);

  const getBridgeTokenValue = async () => {
    try {
      setIsVerificationInProgress(true);

      const { token } = await truvApi.createBridgeToken({
        borrowerId: borrowerId as string,
        applicationId: applicationId as string,
        borrowerType: BorrowerRole.Borrower,
        socialSecurityBenefitType: null,
        useFinancialAccountAggregation: true,
      });

      setCurrentToken(token);
      setIsVerificationWidgetOpened(true);
      setIsVerificationInProgress(false);
    } catch (error) {
      setIsVerificationInProgress(false);
      setIsVerificationWidgetOpened(false);

      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    }
  };

  const handleSuccessVerification = async () => {
    try {
      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: true,
        }),
      );

      setIsVerificationWidgetOpened(false);

      await truvApi.completeFinancialAccountAggregation({
        applicationId: applicationId!,
      });

      dispatch(
        setLoanPortal({
          bankAccountsStatus: BankAccountsStatus.PendingFinancialAccounts,
          financialAccountAggregationAttemptComplete: true,
        }),
      );
    } catch (error) {
      dispatch(setLoanPortal({ financialAccountAggregationAttemptComplete: false }));

      notificationManager.notifyFailure(FailureNotificationType.UnknownError);
    } finally {
      dispatch(
        setLoadingState({
          type: LoadingType.GetBankAccounts,
          loading: false,
        }),
      );
    }
  };

  const handleNoDataEvent = async (type: string, payload?: Record<string, any>) => {
    if (type === VERIFICATION_ERROR_TYPE && payload?.error?.error_code === VERIFICATION_NO_DATA_ERROR_CODE) {
      try {
        dispatch(
          setLoadingState({
            type: LoadingType.GetBankAccounts,
            loading: true,
          }),
        );

        await truvApi.saveVerificationError({
          applicationId: applicationId!,
        });

        await dispatch(getApplicationInfoForDashboardThunk(borrowerId!)).unwrap();
      } catch (error) {
        dispatch(setLoanPortal({ financialAccountAggregationAttemptComplete: false }));

        notificationManager.notifyFailure(FailureNotificationType.UnknownError);
      } finally {
        dispatch(
          setLoadingState({
            type: LoadingType.GetBankAccounts,
            loading: false,
          }),
        );
      }
    }
  };

  const handleVerificationBoxClick = () => {
    if (isVerificationInProgress || financialAccountAggregationAttemptComplete) {
      return;
    }

    return getBridgeTokenValue();
  };

  const connectBankAccountSectionState: SubSectionState = {
    isCompleted: fundingInformationComplete,
    isExist: true,
  };

  return (
    <>
      <Section title={'Funding Information'} subSectionStates={[connectBankAccountSectionState]}>
        <SubSection state={connectBankAccountSectionState}>
          <p className={styles.info}>
            Your bank account will be used for funding your loan proceeds and for autopay (if you have elected for
            autopay).
          </p>
          <div className={styles.connectBankAccountBox}>
            <div className={styles.connectBankAccountBoxContent}>
              <div className={styles.connectBankAccountInfoContainer}>
                <ConnectBankAccountIcon />
                <div className={styles.connectBankAccountInfoContent}>
                  <div className={styles.connectBankAccountLabelContainer}>
                    <div className={styles.connectBankAccountLabel}>Recommended</div>
                    <div className={styles.connectBankAccountLabel}>Fastest</div>
                  </div>
                  <div>Sign in to your bank to verify in seconds</div>
                </div>
              </div>
              <Button
                isLoading={isVerificationInProgress}
                className={styles.connectButton}
                onClick={handleVerificationBoxClick}
              >
                Connect to my Bank Account
              </Button>
            </div>
          </div>
        </SubSection>
      </Section>
      <TruvBridge
        bridgeToken={currentToken}
        onClose={() => setIsVerificationWidgetOpened(false)}
        onSuccess={() => handleSuccessVerification()}
        isOpened={isVerificationWidgetOpened}
        // @ts-ignore: onEvent is not typed in @truv/react but is a valid TruvBridge callback
        onEvent={handleNoDataEvent}
      />
    </>
  );
};

export default ConnectBankAccountSection;
